<script lang="ts" setup>
import { Divider, Checkbox, Message, Skeleton, Button, IftaLabel, InputNumber, Select } from 'primevue';
import { Form, FormField, type FormInstance, type FormSubmitEvent } from '@primevue/forms';
import type { InnerCexCexOrdersModalProps } from '../CexCexOrdersModal.vue';
import SrInputNumber from '@/components/SrInputNumber.vue';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useCexCexTwoLegsSettings } from '@/composable/settings/useCexCexTwoLegsSettings';
import { isNumber } from 'lodash';
import { useCexCexLimitsRefresher } from '@/composable/refreshers/useCexCexLimitsRefresher';
import { useCreateSwapOrdersSettings } from '@/composable/settings/useCreateSwapOrdersSettings';
import { preventEnter } from '@/utils';
import { i18n } from '@/i18n';
import { useProfiles } from '@/composable/useMultiProfiles';
import OrdersLimitsInfo from '@/components/order-modals/OrdersLimitsInfo.vue';
import type { MarketType } from '@/utils/types';
import OrdersCommonInfo from '@/components/order-modals/OrdersCommonInfo.vue';
import TriggerButton from '@/components/TriggerButton.vue';
import { useTriggerSettings } from '@/composable/settings/useTriggerSettings';
// import { useCheckUserRights } from '@/composable/useCheckUserRights';
import { useCexFeatures } from '@/composable/useCexFeatures';
import ProfilesFieldset from '@/components/ProfilesFieldset.vue';
import { useConfirmationsTimer } from '@/composable/refreshers/useConfirmationsTimer';
import { useSound } from '@vueuse/sound'
import { useFlasherStore } from '@/stores/flasherStore'
import bellSoundFile from '@/assets/sounds/alert.wav'

const { t } = i18n.global
const SLIPPAGE_MAX = 20
const SLIPPAGE_MIN = -20
const LEVERAGE_MAX = 10
const LEVERAGE_MIN = 1

const props = defineProps<InnerCexCexOrdersModalProps>()
const emit = defineEmits(['update-user-positions', 'show-positions-modal'])

// Триггер
const bellSound = useSound(bellSoundFile)
const flasherStore = useFlasherStore()

// Кэши
// const cacheKeyPostfix = computed(() => `${props.exBuy}/${props.exSell}/${props.asset}/${props.marketType}`.toLocaleLowerCase())

const cacheKeyPostfix = computed(() => props.cacheKey)
const triggerSpreadCacheKey = computed(() => `trigger/${cacheKeyPostfix.value}`)

// UI-переменные
const settingsForm = useTemplateRef<FormInstance>("settingsForm")
const triggerButton = useTemplateRef("triggerButton")
const approxOrderCost = ref<string>('')
const modeActive = ref(false)
const disablePrepare = computed(() => autoPrepare.value ? isProfilesPrepared.value : false)
const marginModes = [
  {
    label: t('marginModes.isolated'),
    value: 'isolated'
  },
  {
    label: t('marginModes.cross'),
    value: 'cross'
  },
]

// Композаблы
const { toggleProfile, profiles, profilesMax, enabledProfiles } = useProfiles(cacheKeyPostfix, "cex_cex")
const { triggerValue } = useTriggerSettings(cacheKeyPostfix, "cex-cex")
// const { isCexCexAdvanced } = useCheckUserRights()
const isCexCexAdvanced = ref(true)

// Общие настройки
const isAnyProfileChoosen = computed(() => (enabledProfiles.value?.length ?? 0) > 0)
const { useMarketOrder, reduceOnly, closeOnFail, autoPrepare, triggerConfirmations, orderNotifications, maxSignalsCounter } = useCexCexTwoLegsSettings()
// Триггер + подтверждения
const { handleSpreadWatch } = useConfirmationsTimer(maxSignalsCounter)
// Индивидуальные настройки
const { initSettingsForm, leverage, amount, slippage, marginMode } = useCreateSwapOrdersSettings(settingsForm, cacheKeyPostfix)
const { updateLimits, limitsRefresher, limits, limitsLoading } = useCexCexLimitsRefresher(props.exBuy, props.exSell, props.asset, props.marketType, props.sellAsset, props.quoteBuy, props.quoteSell)
const {
  closingPositions,
  cancelingOrders,
  isProfilesPrepared,
  creatingOrders,
  preparingProfiles,
  fetchingBalance,
  prepareProfilesWrapper,
  createOrdersWrapper,
  fetchBalancesWrapper,
  cancelOrdersWrapper,
  closePositionsWrapper
} = useCexFeatures(orderNotifications, enabledProfiles, profilesMax, "cex_cex")


// Работа с формой
const initValues = {
  'amount': null,
  'leverage': null,
  'slippage': null
}
const resolver = (values: any) => { // eslint-disable-line
  return {
    amount: values.amount,
    leverage: values.leverage,
    slippage: values.slippage
  }
}

const fetchBalanceWargs = async () => {
  return await fetchBalancesWrapper(
    props.exBuy as string,
    props.exSell as string,
    props.marketType as MarketType,
    props.quoteBuy as string,
    props.quoteSell as string,
  )
}

const updateOpenPositionsUntilSuccess = () => {
  window.setTimeout(() => {
    emit('update-user-positions')
  }, 3000)
}

const updateClosedPositionsUntilSuccess = () => {
  window.setTimeout(() => {
    emit('update-user-positions')
  }, 3000)
}

const closePositionsWargs = async () => {
  // setInterval(() => emit('update-user-positions'), 5000)
  updateClosedPositionsUntilSuccess()
  return await closePositionsWrapper(
    props.exBuy as string,
    props.exSell as string,
    props.asset as string,
    props.marketType as MarketType,
    props.sellAsset,
    props.quoteBuy,
    props.quoteSell,
    props.priceBuy ?? undefined,
    props.priceSell ?? undefined
  )
}

const cancelOrdersWargs = async () => {
  return await cancelOrdersWrapper(
    props.exBuy as string,
    props.exSell as string,
    props.asset as string,
    props.marketType as MarketType,
    props.sellAsset,
    props.quoteBuy,
    props.quoteSell,
  )
}

const prepareProfilesWargs = async () => {
  console.log(props.quoteBuy)
  console.log(props.quoteSell)
  return await prepareProfilesWrapper(
    props.asset as string,
    props.exBuy as string,
    props.exSell as string,
    leverage.value as number,
    marginMode.value,
    props.marketType as MarketType,
    props.sellAsset,
    props.quoteBuy,
    props.quoteSell,
  )
}

const createOrdersWargs = async () => {
  console.trace('createOrdersWargs called');
  const orderLeverage = leverage.value
  updateOpenPositionsUntilSuccess()
  return await createOrdersWrapper(
    props.asset as string,
    orderLeverage as number,
    props.exBuy as string,
    props.exSell as string,
    amount.value as number,
    slippage.value as number,
    reduceOnly.value,
    props.priceBuy as number,
    props.priceSell as number,
    props.backPriceBuy as number,
    props.backPriceSell as number,
    props.marketType as MarketType,
    marginMode.value,
    closeOnFail.value,
    autoPrepare.value,
    useMarketOrder.value,
    triggerValue,
    triggerButton,
    props.sellAsset,
    props.quoteBuy,
    props.quoteSell,
  )
}

const onTwoLegsFormSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid) {
    return
  }
  await createOrdersWargs()
}

// При изменении amount меняет значение маржи
const onAmountChange = (v: number | null | undefined) => {
  if (!v || !props.priceBuy || !props.priceSell || !isNumber(v)) {
    approxOrderCost.value = ''
  } else {
    const averagePrice = (props.priceBuy + props.priceSell) / 2
    approxOrderCost.value = ` (маржа≈${Number(v * averagePrice / (leverage.value ?? 1)).toFixed(2)} USDT)`
  }
}

const onSpreadTriggered = () => {
  console.log(`onSpreadTriggered called from twoLegs`)
  triggerValue.value = null
  bellSound.play()
  flasherStore.start()
  createOrdersWargs()
}

watch(amount, onAmountChange)
// Следим за спредами для триггера
watch(() => props.spread, (v) => {
  if (v && !reduceOnly.value && modeActive) handleSpreadWatch(v, onSpreadTriggered, triggerValue, triggerConfirmations)
})
watch(() => props.backSpread, (v) => {
  if (v && reduceOnly.value && modeActive) handleSpreadWatch(v, onSpreadTriggered, triggerValue, triggerConfirmations)
})


// Обрабатываем изменение спредов
const onAppears = async () => {
  modeActive.value = true
  console.log('two legs mode+')
  updateLimits()
  limitsRefresher.startRefresh()
  // Подгружаем из кэша данные формы
  initSettingsForm(props.recommendedAmount ?? null)
  onAmountChange(amount.value)
}

const onDisappears = async () => {
  modeActive.value = false
  console.log('two legs mode-')
  limitsRefresher.stopRefresh()
}

defineExpose({
  onAppears,
  onDisappears
})
</script>

<template>

  <div>
    <div v-show="!minimized">
      <!-- Спред -->
      <div class="grid grid-cols-2 px-1 py-1 border-color border-b-1" :class="{ 'muted': reduceOnly }">
        <div class="text-start">{{ $t('modal.createSwapOrders.openSpread') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.spread">{{ Number(props.spread).toFixed(2) }}%</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>
      <!-- Обратный спред -->
      <div class="grid grid-cols-2 px-1 py-1" :class="{ 'muted': !reduceOnly }">
        <div class="text-start">{{ $t('modal.createSwapOrders.closeSpread') }}</div>
        <div class="flex justify-end items-center">
          <span v-if="props.backSpread">{{ Number(props.backSpread).toFixed(2) }}%</span>
          <Skeleton width="40px" v-else></Skeleton>
        </div>
      </div>

      <!-- Данные формы -->
      <Form ref="settingsForm" :initial-values="initValues" class="grid gap-6 md:gap-1 w-full mt-4" :resolver="resolver"
        :class="{ 'md:grid-cols-2': reduceOnly, 'lg:grid-cols-4': !reduceOnly }" @submit="onTwoLegsFormSubmit"
        id="two-legs-form">

        <!-- Объём -->
        <FormField v-slot="$field" class="relative" name="amount">
          <IftaLabel>
            <SrInputNumber @change="onAmountChange" :min="amountStep" type="text" :maxFractionDigits="8"
              placeholder="1000" fluid @keydown="preventEnter" />
            <label>{{ $t('modal.createSwapOrders.amount') }}{{ approxOrderCost }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message) }}
          </Message>
        </FormField>

        <!-- Плечо -->
        <FormField v-slot="$field" class="relative" name="leverage" v-show="!reduceOnly">
          <IftaLabel>
            <SrInputNumber allow-empty showButtons :min="1" :max="10" :step="1" :maxFractionDigits="0" placeholder="1"
              fluid @keydown="preventEnter" />
            <label>{{ $t('modal.createSwapOrders.leverage') }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message,
              { min: LEVERAGE_MIN, max: LEVERAGE_MAX }) }}
          </Message>
        </FormField>

        <!-- Тип маржи -->
        <FormField v-slot="$field" class="relative" name="marginMode" v-show="!reduceOnly">
          <IftaLabel>
            <Select fluid :options="marginModes" option-label="label" option-value="value">
            </Select>
            <label>{{ $t('modal.createSwapOrders.marginMode') }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message) }}
          </Message>
        </FormField>

        <!-- Проскальзывание -->
        <FormField v-slot="$field" class="relative" name="slippage">
          <IftaLabel>
            <SrInputNumber :disabled="useMarketOrder" mode="decimal" showButtons :step="0.1" :maxFractionDigits="2"
              placeholder="3" fluid allow-empty :min="SLIPPAGE_MIN" :max="SLIPPAGE_MAX" @keydown="preventEnter" />
            <label>{{ $t('modal.createSwapOrders.slippage') }}</label>
          </IftaLabel>
          <Message v-if="$field?.invalid" class="absolute" severity="error" size="small" variant="simple">{{
            $t($field.error?.message,
              { min: SLIPPAGE_MIN, max: SLIPPAGE_MAX }) }}
          </Message>
        </FormField>

        <!-- Чекбоксы -->
        <div class="col-span-full mt-4 flex justify-between items-center">
          <div class="flex gap-2 justify-end items-center">
            <Checkbox name="market" v-model="useMarketOrder" binary inputId="market" @keydown="preventEnter" />
            <label for="market">{{ $t("modal.createSwapOrders.marketOrder") }}</label>
          </div>
          <div class="flex gap-2 justify-end items-center">
            <Checkbox name="close" v-model="reduceOnly" binary inputId="close" @keydown="preventEnter" />
            <label for="close">{{ $t("modal.createSwapOrders.close") }}</label>
          </div>
        </div>

      </Form>

      <div class="text-sm">
        <!-- Лимиты -->
        <OrdersLimitsInfo :limits="limits" :limits-loading="limitsLoading" :ex-buy="exBuy" :ex-sell="exSell">
        </OrdersLimitsInfo>
        <Divider class="mb-0 mt-0"></Divider>

        <OrdersCommonInfo :price-sell="priceSell" :price-buy="priceBuy" :amount="amount" :vol-sell="volSell"
          :vol-buy="volBuy" :ex-buy="exBuy" :ex-sell="exSell" :leverage="leverage">

        </OrdersCommonInfo>
        <!-- Общая информация -->
        <Divider class="mb-0 mt-0"></Divider>
      </div>

      <!-- Подготовка профилей и открытие позиции -->
      <div class="grid grid-cols-1 md:grid-cols-2 mt-3 gap-3">
        <div class="grid grid-cols-4">
          <Button @click="autoPrepare = !autoPrepare" class="rounded-r-none border-r-0" severity="success"
            variant="outlined" :icon="autoPrepare ? 'pi pi-check-square' : 'pi pi-stop'"
            :label="$t('modal.createSwapOrders.auto')">
            <!-- <i class="pi" :class="{ 'pi-check-square': autoPrepare, 'pi-stop': !autoPrepare }"></i>
            <span>{{ $t("modal.createSwapOrders.auto") }}</span> -->
          </Button>
          <Button type="button" icon="pi pi-user" @click="prepareProfilesWargs" :loading="preparingProfiles"
            class="col-span-3 rounded-l-none" severity="success"
            :disabled="disablePrepare || preparingProfiles || !isAnyProfileChoosen" variant="outlined"
            :label="$t('modal.createSwapOrders.prepareProfiles')"></Button>
        </div>
        <Button form="two-legs-form" :severity="reduceOnly ? 'danger' : 'success'" type="submit" variant="outlined"
          :loading="creatingOrders" :disabled="creatingOrders || !isAnyProfileChoosen" icon="pi pi-bolt"
          :label="$t(reduceOnly ? 'modal.createSwapOrders.closePositions' : 'modal.createSwapOrders.openPositions')">
        </Button>
      </div>

      <!-- Триггер -->
      <TriggerButton class="mt-3" ref="triggerButton" :root="cacheKeyPostfix" cache-key="cex-cex"
        :trigger-spread-cache-key="triggerSpreadCacheKey" :amount="amount">
      </TriggerButton>

      <!-- "Быстрые" действия -->
      <div class="grid grid-cols-1 md:grid-cols-2 mt-3 gap-3">
        <Button :label="$t('modal.createSwapOrders.quickPosClose')" :loading="closingPositions" icon="pi pi-bolt"
          :disabled="!isAnyProfileChoosen || closingPositions" @click="closePositionsWargs" severity="danger"
          variant="outlined"></Button>
        <Button :label="$t('modal.createSwapOrders.cancelOrders')" :loading="cancelingOrders" icon="pi pi-times-circle"
          :disabled="!isAnyProfileChoosen || cancelingOrders" @click="cancelOrdersWargs" severity="success"
          variant="outlined"></Button>
      </div>

      <!-- Остальные кнопки -->
      <div class="grid grid-cols-1 md:grid-cols-2 mt-3 gap-3">
        <div class="grid grid-cols-6 col-span-2 md:col-span-1">
          <div class="col-span-4 lg:col-span-4 xl:col-span-5">
            <Button fluid :icon="triggerConfirmations ? 'pi pi-check-square' : 'pi pi-stop'"
              @click="triggerConfirmations = !triggerConfirmations" severity="success"
              :variant="triggerConfirmations ? undefined : 'outlined'" class="rounded-r-none"
              :label="$t('modal.createSwapOrders.requireConfirms')">
            </Button>
          </div>
          <div class="col-span-2 lg:col-span-2 xl:col-span-1">
            <InputNumber fluid pt:pcInputText:root:class="rounded-l-none" :min="1" :max="6" :showButtons="true"
              v-model="maxSignalsCounter">
            </InputNumber>
          </div>
        </div>
        <Button class="col-span-1" :icon="orderNotifications ? 'pi pi-check-square' : 'pi pi-stop'"
          @click="orderNotifications = !orderNotifications" severity="success"
          :variant="orderNotifications ? undefined : 'outlined'" :label="$t('modal.createSwapOrders.notifications')">
        </Button>
      </div>

      <!-- Доступны в продвинутой версии -->
      <div v-if="isCexCexAdvanced" class="grid grid-cols-1 mt-3 gap-3"
        :class="{ 'md:grid-cols-3': isCexCexAdvanced, 'md:grid-cols-2': !isCexCexAdvanced }">
        <Button icon="pi pi-money-bill" severity="success" :loading="fetchingBalance"
          :disabled="!isAnyProfileChoosen || fetchingBalance" @click="fetchBalanceWargs" variant="outlined"
          :label="$t('modal.createSwapOrders.fetchBalance')">
        </Button>
        <Button class="col-span-1" :icon="closeOnFail ? 'pi pi-check-square' : 'pi pi-stop'"
          @click="closeOnFail = !closeOnFail" severity="success" :variant="closeOnFail ? undefined : 'outlined'"
          :label="$t('modal.createSwapOrders.closeOnFail')">
        </Button>
        <Button v-if="isCexCexAdvanced" fluid class="col-span-1 lg:col-span-1" severity="success" variant="outlined"
          icon="pi pi-shopping-cart" @click="emit('show-positions-modal')"
          :label="$t('modal.createSwapOrders.myPositions')">
        </Button>
      </div>

      <ProfilesFieldset class="mb-15 md:mb-0" :toggle-profile="toggleProfile" :profiles="profiles"
        :enabled-profiles="enabledProfiles">
      </ProfilesFieldset>
    </div>
    <div v-show="minimized" class="grid grid-cols-1 md:grid-cols-2 gap-2">
      <Button :label="$t('modal.createSwapOrders.quickPosClose')" :loading="closingPositions"
        :disabled="!isAnyProfileChoosen || closingPositions" @click="closePositionsWargs" severity="danger"
        variant="outlined"></Button>
      <Button form="two-legs-form" :severity="reduceOnly ? 'danger' : 'success'" type="submit" variant="outlined"
        :loading="creatingOrders" :disabled="creatingOrders || !isAnyProfileChoosen"
        :label="$t(reduceOnly ? 'modal.createSwapOrders.closePositions' : 'modal.createSwapOrders.openPositions')">
      </Button>
    </div>
  </div>
</template>
